import { Popover } from '@headlessui/react';
import { mainClient } from 'apollo/client';
import { DetailPageClientContext } from 'components/desktop/detail';
import SharePostPopover from 'components/desktop/detail/main-section/main-section-right/creator-information/share-post-popover';
import ViewHouseCalendarModal from 'components/desktop/detail/main-section/main-section-right/creator-information/view-house-calendar-modal';
import FriendActions from 'components/desktop/shared/friend-actions';
import SaveRealEstateModal from 'components/desktop/shared/save-real-estate-modal';
import ToastStack from 'components/desktop/shared/toast-stack';
import {
  IconCalendar,
  IconComment,
  IconHeart,
  IconHeartOutline,
  IconLocationFill,
  IconPhone,
  IconShare,
} from 'components/icons';
import { useAuthenticationContext } from 'components/providers/authentication-provider';
import ModalWrapper from 'components/shared/modal-wrapper';
import NotificationModal from 'components/shared/notification-modal';
import { SITE_MAP } from 'globalConstants';
import { FileWithFullUrls, GET_REAL_ESTATE_DETAILS, UserUsers } from 'graphql/main/queries';
import { LeadRealEstateStatus } from 'graphql/main/queries/get-list-lead-real-estates';
import { useModal, useToast, useTranslation } from 'hooks';
import { gt, includes, isArray, isEmpty, isEqual, isString, isUndefined, toString } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { DetailPageContext } from 'pages/[ket-qua-tim-kiem]/[chi-tiet-tin-rao]';
import { Fragment, useContext, useState } from 'react';
import ContactForeclosureModal from './contact-foreclosure-modal';
import GuestReportModal from './guest-report-modal';

const CreatorInformation = () => {
  const { push } = useRouter();
  const translation = useTranslation();
  const { toasts, addToast, removeToast } = useToast();
  const { showing: showingViewHouseCalendarModal, toggle: toggleViewHouseCalendarModal } =
    useModal();
  const { showing: showingContactForeclosurerModal, toggle: toggleContactForeclosurerModal } =
    useModal();
  const { showing: showingSavePostModal, toggle: toggleSavePostModal } = useModal();
  const { showing: showingCreateGuestReportModal, toggle: toggleCreateGuestReportModal } =
    useModal();
  const { showing: showingViewGuestReportWaringModal, toggle: toggleViewGuestReportWaringModal } =
    useModal();
  const { showing: showingViewContractWaringModal, toggle: toggleViewContractWaringModal } =
    useModal();
  const {
    showing: showingCreateGuestReportWaringModal,
    toggle: toggleCreateGuestReportWaringModal,
  } = useModal();
  const { showing: showingCreateContractWaringModal, toggle: toggleCreateContractWaringModal } =
    useModal();
  const { currentUser } = useAuthenticationContext();
  const { realEstate } = useContext(DetailPageContext).pageData ?? {};
  const { realEstate: realEstateClient } = useContext(DetailPageClientContext).pageData ?? {};
  const {
    id,
    creator,
    contactPhones,
    numberOfRemaining,
    realEstateId,
    contactName,
    isForeclosure,
    isLeadRE,
    leadREStatus,
    contactInfo,
    contractCount,
    enableAction,
  } = realEstate ?? {};
  const { isSaved, guestReportCount } = realEstateClient ?? {};
  const [showingPhoneNumber, setShowingPhoneNumber] = useState(false);
  const [isPhoneNumberCopied, setIsPhoneNumberCopied] = useState(false);
  const [guestReportWarningText, setGuestReportWarningText] = useState('');
  const [contractWarningText, setContractWarningText] = useState('');

  const isMyPost = isEqual(currentUser?.id, contactInfo?.id);

  const copyPhoneNumber = (phones?: string[] | string) => {
    if (phones) {
      if (isArray(phones) && phones[0]) {
        navigator.clipboard.writeText(phones[0]);
      }
      if (isString(phones)) {
        navigator.clipboard.writeText(phones);
      }
      setIsPhoneNumberCopied(true);
      addToast('success', 'Sao chép số điện thoại thành công');
      const copyTimeout = setTimeout(() => {
        setIsPhoneNumberCopied(false);
        clearTimeout(copyTimeout);
      }, 2 * 1000);
    }
  };
  const comment = () => {
    addToast('information', 'Chức năng đang được hoàn thiện!');
  };
  const createGuestReport = () => {
    if (includes([LeadRealEstateStatus.sold, LeadRealEstateStatus.inDispute], leadREStatus)) {
      toggleCreateGuestReportWaringModal();
      setGuestReportWarningText(
        `Hiện tại BĐS đầu chủ này ${
          translation.major.leadRealEstateStatus[toString(leadREStatus)]
        } nên không thể tạo báo cáo dẫn khách`,
      );
    } else if (!enableAction) {
      toggleCreateGuestReportWaringModal();
      setGuestReportWarningText(
        'Hiện đã có báo cáo dẫn khách được duyệt nên không được phép tạo báo cáo dẫn khách',
      );
    } else {
      toggleCreateGuestReportModal();
    }
  };
  const viewGuestReport = () => {
    if (guestReportCount) {
      push(`/${SITE_MAP.LEAD_REAL_ESTATE.INDEX}/${SITE_MAP.LEAD_REAL_ESTATE.GUEST_REPORT.INDEX}`);
    } else {
      toggleViewGuestReportWaringModal();
    }
  };
  const createContract = () => {
    if (includes([LeadRealEstateStatus.sold, LeadRealEstateStatus.inDispute], leadREStatus)) {
      toggleCreateContractWaringModal();
      setContractWarningText(
        `Hiện tại BĐS đầu chủ này ${
          translation.major.leadRealEstateStatus[toString(leadREStatus)]
        } nên không thể thêm hợp đồng`,
      );
    } else if (!enableAction) {
      toggleCreateContractWaringModal();
      setContractWarningText('Hiện đã có hợp đồng được duyệt nên không được phép thêm hợp đồng');
    } else {
      push(`/${SITE_MAP.CREATE_CONTRACT.INDEX}/${id}`);
    }
  };
  const viewContract = () => {
    if (contractCount) {
      push(`/${SITE_MAP.LEAD_REAL_ESTATE.INDEX}/${SITE_MAP.LEAD_REAL_ESTATE.CONTRACT.INDEX}`);
    } else {
      toggleViewContractWaringModal();
    }
  };
  const createGuestReportComplete = () => {
    addToast('success', 'Thêm báo cáo dẫn khách thành công');
    mainClient.refetchQueries({ include: [GET_REAL_ESTATE_DETAILS] });
  };
  const createGuestReportError = () => {
    addToast('error', 'Thêm báo cáo dẫn khách thất bại');
  };

  return (
    <Fragment>
      <div className='flex flex-col space-y-[20px] rounded-[16px] bg-paper px-[24px] py-[28px] shadow-1'>
        <span className='text-[16px] font-[600] leading-[20px]'>Liên hệ</span>
        <div className='flex flex-col space-y-[24px]'>
          {isForeclosure ? (
            <Fragment>
              <div className='flex space-x-[12px]'>
                <Link href={`/${SITE_MAP.LIST_BROKER.INDEX}/${contactInfo?.id}`}>
                  <div className='h-[72px] w-[72px] cursor-pointer overflow-hidden rounded-full'>
                    <img
                      src={
                        (contactInfo?.avatar as FileWithFullUrls)?.originalUrl ??
                        '/images/default-avatar.svg'
                      }
                      alt={contactName}
                      loading='lazy'
                      className='h-full w-full object-cover'
                    />
                  </div>
                </Link>
                <div className='flex flex-col space-y-[8px]'>
                  {!isEmpty(contactName) && (
                    <span className='text-[16px] font-[600] leading-[20px]'>{contactName}</span>
                  )}
                  {(!isEmpty(contactInfo?.type) || !isEmpty(contactInfo?.province)) && (
                    <div className='flex items-center space-x-[8px] text-[12px] leading-[15px] text-text2'>
                      {contactInfo?.type && (
                        <span>{(translation.major.b2cUserTypes as any)[contactInfo.type]}</span>
                      )}
                      <Fragment>
                        {!isEmpty(contactInfo?.type) && !isEmpty(contactInfo?.province?.name) && (
                          <span>{'\u2022'}</span>
                        )}
                        <div className='flex items-center space-x-[4px]'>
                          <IconLocationFill className='h-[16px] w-[16px]' />
                          <span>
                            {contactInfo?.province ? contactInfo.province.name : 'Đang cập nhật'}
                          </span>
                        </div>
                      </Fragment>
                    </div>
                  )}
                  {gt(numberOfRemaining, 0) && (
                    <Link href={`/${SITE_MAP.LIST_BROKER.INDEX}/${creator?.id}`}>
                      <span className='leading-[23px] text-primary'>{`Xem thêm ${numberOfRemaining} tin đăng khác`}</span>
                    </Link>
                  )}
                </div>
              </div>
              {contactPhones && !isEmpty(contactPhones) && (
                <button
                  type='button'
                  className='flex h-[40px] items-center justify-center space-x-[15px] rounded-[8px] border border-primary bg-secondary px-[28px] py-[12px] text-primary transition duration-[200ms] ease-in-out hover:bg-secondary-light'
                  onClick={() => {
                    if (!showingPhoneNumber) {
                      setShowingPhoneNumber(true);
                    } else {
                      copyPhoneNumber(contactPhones[0]);
                    }
                  }}
                >
                  <IconPhone className='h-[23px] w-[23px] text-primary' />
                  <span className='truncate font-[600]'>
                    {showingPhoneNumber && contactPhones && !isEmpty(contactPhones)
                      ? `${contactPhones[0]} . ${isPhoneNumberCopied ? 'Đã sao chép' : 'Sao chép'}`
                      : `${toString(contactPhones[0]?.slice(0, 6))}
        **** . Hiện số`}
                  </span>
                </button>
              )}
              {!isUndefined(currentUser) && !isMyPost && (
                <Fragment>
                  <button
                    type='button'
                    className='flex h-[40px] items-center justify-center space-x-[15px] rounded-[8px] bg-tertiary px-[28px] py-[12px] transition duration-[200ms] ease-in-out hover:bg-tertiary-light'
                    onClick={toggleContactForeclosurerModal}
                  >
                    <IconCalendar className='min-h-[24px] min-w-[24px]' />
                    <span className='font-[600]'>Liên hệ phát mãi</span>
                  </button>
                  <ModalWrapper position='center' showing={showingContactForeclosurerModal}>
                    <ContactForeclosureModal onClose={toggleContactForeclosurerModal} />
                  </ModalWrapper>
                </Fragment>
              )}
            </Fragment>
          ) : isLeadRE ? (
            <Fragment>
              <div className='flex space-x-[12px]'>
                <Link href={`/${SITE_MAP.LIST_BROKER.INDEX}/${contactInfo?.id}`}>
                  <div className='h-[72px] w-[72px] cursor-pointer overflow-hidden rounded-full'>
                    <img
                      src={
                        (contactInfo?.avatar as FileWithFullUrls)?.originalUrl ??
                        '/images/default-avatar.svg'
                      }
                      alt={contactInfo?.fullName}
                      loading='lazy'
                      className='h-full w-full object-cover'
                    />
                  </div>
                </Link>
                <div className='flex flex-col space-y-[8px]'>
                  {!isEmpty(realEstateId)
                    ? !isEmpty(contactInfo?.fullName) && (
                        <span className='text-[16px] font-[600] leading-[20px]'>
                          {contactInfo?.fullName}
                        </span>
                      )
                    : !isEmpty(contactName) && (
                        <span className='text-[16px] font-[600] leading-[20px]'>{contactName}</span>
                      )}
                  {(!isEmpty(contactInfo?.type) || !isEmpty(contactInfo?.province)) && (
                    <div className='flex items-center space-x-[8px] text-[12px] leading-[15px] text-text2'>
                      {contactInfo?.type && (
                        <span>{(translation.major.b2cUserTypes as any)[contactInfo.type]}</span>
                      )}
                      <Fragment>
                        <span>{'\u2022'}</span>
                        <div className='flex items-center space-x-[4px]'>
                          <IconLocationFill className='h-[16px] w-[16px]' />
                          <span>
                            {contactInfo?.province ? contactInfo.province.name : 'Đang cập nhật'}
                          </span>
                        </div>
                      </Fragment>
                    </div>
                  )}
                  {gt(numberOfRemaining, 0) && (
                    <Link href={`/${SITE_MAP.LIST_BROKER.INDEX}/${creator?.id}`}>
                      <span className='leading-[23px] text-primary'>{`Xem thêm ${numberOfRemaining} tin đăng khác`}</span>
                    </Link>
                  )}
                </div>
              </div>
              {!isEmpty(contactPhones?.[0]) && (
                <button
                  type='button'
                  className='flex h-[40px] items-center justify-center space-x-[15px] rounded-[8px] border border-primary bg-secondary px-[28px] py-[12px] text-primary transition duration-[200ms] ease-in-out hover:bg-secondary-light'
                  onClick={() => {
                    if (!showingPhoneNumber) {
                      setShowingPhoneNumber(true);
                    } else {
                      copyPhoneNumber(contactPhones);
                    }
                  }}
                >
                  <IconPhone className='h-[23px] w-[23px] text-primary' />
                  <span className='truncate font-[600]'>
                    {showingPhoneNumber
                      ? `${contactPhones?.[0]} . ${
                          isPhoneNumberCopied ? 'Đã sao chép' : 'Sao chép'
                        }`
                      : `${toString(contactPhones?.[0]?.slice(0, 6))}
        **** . Hiện số`}
                  </span>
                </button>
              )}
              {!isUndefined(currentUser) && !isMyPost && (
                <div className='flex flex-col space-y-[16px]'>
                  <div className='flex flex-col space-y-[16px] py-[16px] border-y border-stroke'>
                    <button
                      type='button'
                      className='flex h-[40px] items-center justify-center space-x-[15px] rounded-[8px] bg-tertiary px-[28px] py-[12px] transition duration-[200ms] ease-in-out hover:bg-tertiary-light'
                      onClick={createGuestReport}
                    >
                      <span className='font-[600]'>Tạo báo cáo dẫn khách</span>
                    </button>
                    <button
                      type='button'
                      className='flex h-[40px] items-center justify-center space-x-[15px] rounded-[8px] bg-tertiary px-[28px] py-[12px] transition duration-[200ms] ease-in-out hover:bg-tertiary-light'
                      onClick={viewGuestReport}
                    >
                      <span className='font-[600]'>{`Xem báo cáo dẫn khách (${
                        guestReportCount ?? 0
                      })`}</span>
                    </button>
                  </div>
                  <div className='flex flex-col space-y-[16px] pb-[16px] border-b border-stroke'>
                    <button
                      type='button'
                      className='flex h-[40px] items-center justify-center space-x-[15px] rounded-[8px] bg-tertiary px-[28px] py-[12px] transition duration-[200ms] ease-in-out hover:bg-tertiary-light'
                      onClick={createContract}
                    >
                      <span className='font-[600]'>Thêm hợp đồng BĐS</span>
                    </button>
                    <button
                      type='button'
                      className='flex h-[40px] items-center justify-center space-x-[15px] rounded-[8px] bg-tertiary px-[28px] py-[12px] transition duration-[200ms] ease-in-out hover:bg-tertiary-light'
                      onClick={viewContract}
                    >
                      <span className='font-[600]'>{`Xem hợp đồng BĐS (${
                        contractCount ?? 0
                      })`}</span>
                    </button>
                  </div>
                </div>
              )}
              <ModalWrapper position='center' showing={showingCreateGuestReportModal}>
                <GuestReportModal
                  onClose={toggleCreateGuestReportModal}
                  onComplete={createGuestReportComplete}
                  onError={createGuestReportError}
                />
              </ModalWrapper>
              <ModalWrapper showing={showingViewGuestReportWaringModal}>
                <NotificationModal
                  type='warning'
                  title='Bạn chưa có báo cáo dẫn khách đối với tin đăng này'
                  textDescription='Tạo báo cáo dẫn khách ngay?'
                  confirmText='Tạo báo cáo'
                  onConfirm={() => {
                    toggleViewGuestReportWaringModal();
                    createGuestReport();
                  }}
                  onCancel={toggleViewGuestReportWaringModal}
                />
              </ModalWrapper>
              <ModalWrapper showing={showingCreateGuestReportWaringModal}>
                <NotificationModal
                  isHiddenCancelButton
                  type='warning'
                  title='Không thể tạo báo cáo dẫn khách'
                  textDescription={guestReportWarningText}
                  onConfirm={toggleCreateGuestReportWaringModal}
                />
              </ModalWrapper>
              <ModalWrapper showing={showingViewContractWaringModal}>
                <NotificationModal
                  type='warning'
                  title='Bạn chưa có hợp đồng BĐS đối với tin đăng này'
                  textDescription='Thêm hợp đồng BĐS ngay?'
                  confirmText='Thêm hợp đồng'
                  onConfirm={() => {
                    toggleViewContractWaringModal();
                    createContract();
                  }}
                  onCancel={toggleViewContractWaringModal}
                />
              </ModalWrapper>
              <ModalWrapper showing={showingCreateContractWaringModal}>
                <NotificationModal
                  isHiddenCancelButton
                  type='warning'
                  title='Không thể thêm hợp đồng'
                  textDescription={contractWarningText}
                  onConfirm={toggleCreateContractWaringModal}
                />
              </ModalWrapper>
            </Fragment>
          ) : (
            <Fragment>
              <div className='flex space-x-[12px]'>
                <Link href={`/${SITE_MAP.LIST_BROKER.INDEX}/${creator?.id}`}>
                  <div className='h-[72px] w-[72px] cursor-pointer overflow-hidden rounded-full'>
                    <img
                      src={
                        (creator?.avatar as FileWithFullUrls)?.originalUrl ??
                        '/images/default-avatar.svg'
                      }
                      alt={creator?.fullName}
                      loading='lazy'
                      className='h-full w-full object-cover'
                    />
                  </div>
                </Link>
                <div className='flex flex-col space-y-[8px]'>
                  {isEmpty(realEstateId)
                    ? !isEmpty(creator?.fullName) && (
                        <span className='text-[16px] font-[600] leading-[20px]'>
                          {creator?.fullName}
                        </span>
                      )
                    : !isEmpty(contactName) && (
                        <span className='text-[16px] font-[600] leading-[20px]'>{contactName}</span>
                      )}
                  {(!isEmpty(creator?.type) || !isEmpty(creator?.province)) && (
                    <div className='flex items-center space-x-[8px] text-[12px] leading-[15px] text-text2'>
                      {creator?.type && (
                        <span>{(translation.major.b2cUserTypes as any)[creator.type]}</span>
                      )}
                      <Fragment>
                        <span>{'\u2022'}</span>
                        <div className='flex items-center space-x-[4px]'>
                          <IconLocationFill className='h-[16px] w-[16px]' />
                          <span>{creator?.province ? creator.province.name : 'Đang cập nhật'}</span>
                        </div>
                      </Fragment>
                    </div>
                  )}
                  {gt(numberOfRemaining, 0) && (
                    <Link href={`/${SITE_MAP.LIST_BROKER.INDEX}/${creator?.id}`}>
                      <span className='leading-[23px] text-primary'>{`Xem thêm ${numberOfRemaining} tin đăng khác`}</span>
                    </Link>
                  )}
                </div>
              </div>
              {!isEmpty(contactPhones?.[0]) && (
                <button
                  type='button'
                  className='flex h-[40px] items-center justify-center space-x-[15px] rounded-[8px] border border-primary bg-secondary px-[28px] py-[12px] text-primary transition duration-[200ms] ease-in-out hover:bg-secondary-light'
                  onClick={() => {
                    if (!showingPhoneNumber) {
                      setShowingPhoneNumber(true);
                    } else {
                      copyPhoneNumber(contactPhones);
                    }
                  }}
                >
                  <IconPhone className='h-[23px] w-[23px] text-primary' />
                  <span className='truncate font-[600]'>
                    {showingPhoneNumber
                      ? `${contactPhones?.[0]} . ${
                          isPhoneNumberCopied ? 'Đã sao chép' : 'Sao chép'
                        }`
                      : `${toString(contactPhones?.[0]?.slice(0, 6))}
        **** . Hiện số`}
                  </span>
                </button>
              )}
              {!isUndefined(currentUser) && !isMyPost && (
                <Fragment>
                  <button
                    type='button'
                    className='flex h-[40px] items-center justify-center space-x-[15px] rounded-[8px] bg-tertiary px-[28px] py-[12px] transition duration-[200ms] ease-in-out hover:bg-tertiary-light'
                    onClick={toggleViewHouseCalendarModal}
                  >
                    <IconCalendar className='min-h-[24px] min-w-[24px]' />
                    <span className='font-[600]'>Đăng ký xem nhà</span>
                  </button>
                  <ModalWrapper position='center' showing={showingViewHouseCalendarModal}>
                    <ViewHouseCalendarModal onClose={toggleViewHouseCalendarModal} />
                  </ModalWrapper>
                </Fragment>
              )}
            </Fragment>
          )}
          {!isUndefined(currentUser) && !isMyPost && <FriendActions user={creator as UserUsers} />}
          <div className='relative flex items-center justify-between space-x-[16px]'>
            {currentUser && (
              <Fragment>
                <button
                  type='button'
                  className='flex w-full items-center justify-center space-x-[6px]'
                  onClick={toggleSavePostModal}
                >
                  <div
                    className={`flex h-[20px] w-[20px] items-center justify-center rounded-full ${
                      isSaved ? 'bg-primary' : 'bg-[#000000]/[.2]'
                    }`}
                  >
                    {isSaved ? (
                      <IconHeart className='h-[12px] w-[12px] text-paper' />
                    ) : (
                      <IconHeartOutline className='h-[12px] w-[12px] text-paper' />
                    )}
                  </div>
                  <span className='text-[12px] font-[600] leading-[15px]'>
                    {isSaved ? 'Tin đã lưu' : 'Lưu lại tin'}
                  </span>
                </button>
                <div className='h-[24px] w-[1px] bg-text2' />
                <ModalWrapper showing={showingSavePostModal}>
                  <SaveRealEstateModal
                    realEstate={realEstateClient}
                    onCompleteSaving={() =>
                      mainClient.refetchQueries({ include: [GET_REAL_ESTATE_DETAILS] })
                    }
                    onClose={toggleSavePostModal}
                  />
                </ModalWrapper>
              </Fragment>
            )}
            <Popover className='relative w-full'>
              {({ open }) => (
                <Fragment>
                  <Popover.Button
                    type='button'
                    className='flex w-full items-center justify-center space-x-[4px]'
                  >
                    <IconShare className='h-[20px] w-[20px] text-primary' />
                    <span className='text-[12px] font-[600] leading-[15px]'>Chia sẻ tin</span>
                  </Popover.Button>
                  {
                    <div className={`${open ? 'block' : 'hidden'}`}>
                      <SharePostPopover />
                    </div>
                  }
                </Fragment>
              )}
            </Popover>
            <div className='h-[24px] w-[1px] bg-text2' />
            <button
              type='button'
              className='flex w-full items-center justify-center space-x-[6px] p-[4px]'
              onClick={comment}
            >
              <IconComment className='h-[20px] w-[20px] text-primary' />
              <span className='text-[12px] font-[600] leading-[15px]'>Bình luận</span>
            </button>
          </div>
        </div>
      </div>
      <ToastStack toasts={toasts} removeToast={removeToast} />
    </Fragment>
  );
};

export default CreatorInformation;
